// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { forwardRef, useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useEvent } from "react-use"
import { t } from "@lingui/macro"
import styled from "styled-components/macro"
import { DropdownContent, List } from "features/shared/search-dropdown/styles"
import { SecondaryButton } from "../../shared/styles"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineActions from "../actions"
import { useDropdownContentHandling } from "../../shared/search-dropdown/useDropdownContentHandling"
import * as UserContextSlice from "../../user-context/slice"
import AddToGroupDropdownListItem from "./AddToGroupDropdownListItem"
import { matchTimeGroupIdList, narrativeType } from "../timeGroups"
import {
  AddToTimeGroupRequest,
  CreateTimeGroupRequest,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { RootState } from "../../../app/store"

const StyledList = styled(props => <List {...props} />)`
  && {
    min-height: 0;
    color: ${({ theme }): string => theme.colors.charcoal};
  }
`

const StyledListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: 600;
  height: ${({ theme }): string => theme.AddToGroupListHeaderHeight}px;
  line-height: ${({ theme }): string => theme.AddToGroupListHeaderHeight}px;
  padding: 0 1rem 0 2rem;
  border-bottom: 1px solid ${({ theme }): string => theme.colors.mercury};
`

const StyledSecondaryButton = styled(SecondaryButton)`
  font-size: 1.1rem;
  font-weight: 500;
  padding: 2px 4px;
`

type Props = {
  style?: React.CSSProperties,
  timeRowId: string,
  setIsDropdownVisible: (isVisible: boolean) => void,
}

const AddToGroupDropdownContent = forwardRef<HTMLDivElement, Props>(({
  style = {},
  timeRowId,
  setIsDropdownVisible,
}: Props, ref): React.ReactElement<Props> | null => {
  const dispatch = useDispatch()
  const {
    activeIdIndex,
    setActiveIdIndex,
    activeId,
    setActiveId,
    handleArrowNavigation,
  } = useDropdownContentHandling()

  const timeGroups = useSelector(TimelineSlice.selectAvailableTimeGroups)
  const currentSite = useSelector(UserContextSlice.selectCurrentSite)
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const makeSelectTimeRow = useMemo(TimelineSlice.selectTimeRow, [])
  const timeRow = useSelector((state: RootState) => makeSelectTimeRow(state, timeRowId))
  const timeRowIdsInGroupingProgress = useSelector(TimelineSlice.selectTimeRowIdsInGroupingProgress)
  const isTimeRowInGroupingProgress = timeRowIdsInGroupingProgress.includes(timeRowId)
  const timeGroupsList = timeGroups.map(({
    id,
    name,
    narrative,
    tagsList,
    tagCategoriesList,
    activityTypesList,
  }) => ({
    id,
    label: name ?? "",
    narrative,
    tags: tagsList.filter(({ siteId }) => siteId === currentSiteId),
    tagCategories: tagCategoriesList.filter(({ siteId }) => siteId === currentSiteId),
    activityTypes: activityTypesList.filter(({ siteId }) => siteId === currentSiteId),
  }))

  const onMouseMove = (): void => {
    if (activeIdIndex === undefined && activeId === undefined) {
      return
    }
    setActiveIdIndex(undefined)
    setActiveId(undefined)
  }

  const handleAddToGroup = (id: string) => {
    dispatch(TimelineActions.addToTimeGroup(
      new AddToTimeGroupRequest()
        .setTimeGroupId(id)
        .setTimeRowIdsList([timeRowId]),
    ))
    setIsDropdownVisible(false)
  }
  
  const createGroup = (): void => {
    if (isTimeRowInGroupingProgress) {
      return
    }
    const request = new CreateTimeGroupRequest()
      .setTimeRowIdsList([timeRowId])
      .setNarrativeType(narrativeType(currentSite))
    dispatch(TimelineActions.createTimeGroup({ request }))
    dispatch(TimelineSlice.addTimeRowIdsInGroupingProgress(request.getTimeRowIdsList()))
  }

  const onKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === "Tab") {
      e.preventDefault()
    }
    if (e.key === "Escape") {
      setIsDropdownVisible(false)
    }
    if (activeId && (e.key === "Enter")) {
      handleAddToGroup(activeId)
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      handleArrowNavigation(
        "ArrowDown",
        timeGroupsList,
      )
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      handleArrowNavigation(
        "ArrowUp",
        timeGroupsList,
      )
    }
  }, [
    timeGroupsList,
  ])

  useEvent("keydown", onKeyDown)

  const matchedTimeGroupIdList = matchTimeGroupIdList(timeRow ? [timeRow] : [], timeGroupsList, currentSiteId)

  return (
    <DropdownContent ref={ref} style={style} onMouseMove={onMouseMove}>
      <StyledListHeader>
        {t({ id: "label_add_to_timesheet", message: "Add to Timesheet" })}
        <StyledSecondaryButton
          type="button"
          onClick={createGroup}
        >
          {t({ id: "label_new_entry", message: "New Entry" })}
        </StyledSecondaryButton>
      </StyledListHeader>
      <StyledList>
        {timeGroupsList.map(listItem => (
          <AddToGroupDropdownListItem
            key={listItem.id}
            listItem={listItem}
            activeId={activeId}
            isMatch={matchedTimeGroupIdList.includes(listItem.id)}
            handleAddToGroup={handleAddToGroup}
          />
        ))}
      </StyledList>
    </DropdownContent>
  )
})

export default AddToGroupDropdownContent
